import type { V2TranscriptWord } from "../V2TextEditor";

export type AIFillerWordsCandidatePayload = {
    id: string;
    label: string;
    phrases: string[];
};

export type AIFillerWordsTokenPayload = {
    tokenId: string;
    text: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    speakerId?: string;
    speakerName?: string;
};

export type AIFillerWordsDetectionPayload = {
    id: string;
    categoryId: string;
    startTokenId: string;
    endTokenId: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    matchedText: string;
    confidence: number;
    isRepeatedWord?: boolean;
    reason?: string;
};

export type AIFillerWordsAmbiguousCandidatePayload = {
    id: string;
    categoryId: string;
    startTokenId: string;
    endTokenId: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    matchedText: string;
    contextLeft: string;
    contextRight: string;
    features?: {
        clauseInitial?: boolean;
        sentenceInitial?: boolean;
        utteranceFinal?: boolean;
        standalone?: boolean;
        commaBounded?: boolean;
        questionAdjacent?: boolean;
        shortResponseTurn?: boolean;
        turnWordCount?: number;
        likelyLexicalLike?: boolean;
        repeatedWord?: boolean;
        repetitionLength?: number;
        previousToken?: string;
        nextToken?: string;
    };
};

export type AIFillerWordsDecisionPayload = {
    id: string;
    decision: "REMOVE" | "KEEP" | "UNSURE";
    confidence?: number;
    reason?: string;
};

export type PostAiFillerWordsFn = (args: {
    projectId: string;
    maxDetections?: number;
    candidates?: AIFillerWordsCandidatePayload[];
    transcript?: {
        text: string;
        tokens: AIFillerWordsTokenPayload[];
    };
    ambiguousCandidates?: AIFillerWordsAmbiguousCandidatePayload[];
}) => Promise<
    | {
          detections?: AIFillerWordsDetectionPayload[];
          decisions?: AIFillerWordsDecisionPayload[];
      }
    | undefined
>;

export type FillerWordTokenizedWord = V2TranscriptWord & {
    tokenId: string;
};

export const buildFillerWordsTranscriptText = (words: FillerWordTokenizedWord[]): string => {
    if (!Array.isArray(words) || words.length === 0) return "";

    let out = "";
    let prevSource = -1;
    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const text = String(w?.text ?? "").trim();
        if (!text) continue;

        const sourceIndex = Number.isFinite(w?.sourceIndex) ? Number(w.sourceIndex) : 0;
        if (out) {
            if (sourceIndex !== prevSource) out += "\n";
            else out += " ";
        }
        out += text;
        prevSource = sourceIndex;
    }

    return out.trim();
};

export const detectFillerWordsWithLLM = async (args: {
    projectId: string;
    words: FillerWordTokenizedWord[];
    candidates: AIFillerWordsCandidatePayload[];
    postAiFillerWords: PostAiFillerWordsFn;
    maxDetections?: number;
}): Promise<AIFillerWordsDetectionPayload[]> => {
    const words = Array.isArray(args.words) ? args.words : [];
    const candidates = Array.isArray(args.candidates) ? args.candidates : [];
    if (!args.postAiFillerWords || !words.length || !candidates.length) return [];

    const tokens: AIFillerWordsTokenPayload[] = words.map((w) => ({
        tokenId: String(w.tokenId),
        text: String(w.text ?? ""),
        startSec: Number(w.startSec ?? 0),
        endSec: Number(w.endSec ?? w.startSec ?? 0),
        sourceIndex: Number(w.sourceIndex ?? 0),
        speakerName: typeof w.speaker === "string" ? w.speaker : undefined,
    }));

    const transcriptText = buildFillerWordsTranscriptText(words);
    const res = await args.postAiFillerWords({
        projectId: String(args.projectId ?? ""),
        maxDetections: args.maxDetections,
        candidates,
        transcript: {
            text: transcriptText,
            tokens,
        },
    });

    if (!Array.isArray(res?.detections)) return [];
    return res!.detections;
};

export const classifyAmbiguousFillerWords = async (args: {
    projectId: string;
    candidates: AIFillerWordsAmbiguousCandidatePayload[];
    postAiFillerWords: PostAiFillerWordsFn;
    maxDetections?: number;
}): Promise<AIFillerWordsDecisionPayload[]> => {
    const candidates = Array.isArray(args.candidates) ? args.candidates : [];
    if (!args.postAiFillerWords || !candidates.length) return [];

    const res = await args.postAiFillerWords({
        projectId: String(args.projectId ?? ""),
        maxDetections: args.maxDetections,
        ambiguousCandidates: candidates,
    });

    if (!Array.isArray(res?.decisions)) return [];
    return res!.decisions;
};
